/**
 * SecureRTO - Evaluation engine for real hardware data.
 *
 * Hardware only sends raw values. Scores are computed here, either against
 * absolute defaults or (preferably) against the reference profile built from
 * tests flagged `storeAsReference`.
 *
 * Scores: turns 35% | smoothness 25% | stability 25% | time 15%
 * PASS = overall >= 70 AND correct turn sequence.
 */


import { normalizeTest } from "./sensorFormat";
import { extractTurns, evaluateTurns } from "./turns";
const ENV = {}; // tunables (same defaults as the Cloud Functions)

const PASS_THRESHOLD = 70;
const HARSH_G = Number(ENV.HARSH_G || 0.25);
const WEIGHTS = { turns: 0.35, smoothness: 0.25, stability: 0.25, time: 0.15 };

// Absolute fallbacks used when no reference profile exists. good = full marks, limit = 70 pts.
const DEFAULT_LIMITS = {
  jerkRms: { good: 1.0, limit: 2.5 }, // g/s
  peakAccelG: { good: 0.3, limit: 0.6 }, // g
  harshCount: { good: 0, limit: 3 },
  rollRms: { good: 15, limit: 40 }, // deg/s
  pitchRms: { good: 15, limit: 40 }, // deg/s
  vibrationG: { good: 0.05, limit: 0.15 }, // g (std of accel magnitude)
};
const DEFAULT_TIME = { minSec: 10, maxSec: 120 };

const LABELS = {
  jerkRms: "Jerk (RMS)",
  peakAccelG: "Peak acceleration",
  harshCount: "Harsh events",
  rollRms: "Roll wobble (gyroX RMS)",
  pitchRms: "Pitch wobble (gyroY RMS)",
  vibrationG: "Vibration",
};

const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const rms = (xs) => (xs.length ? Math.sqrt(mean(xs.map((x) => x * x))) : 0);
const std = (xs) => {
  const m = mean(xs);
  return xs.length ? Math.sqrt(mean(xs.map((x) => (x - m) ** 2))) : 0;
};
const round = (v, d = 3) => Math.round(v * 10 ** d) / 10 ** d;

/** Raw features from the normalised series (independent of any profile). */
function computeFeatures(series, session) {
  const dt = session.dtSec || 0.5;
  const jerks = [];
  for (let i = 1; i < series.length; i++) {
    const a = series[i];
    const b = series[i - 1];
    jerks.push(Math.sqrt((a.ax - b.ax) ** 2 + (a.ay - b.ay) ** 2 + (a.az - b.az) ** 2) / dt);
  }
  const mags = series.map((p) => p.aMag || 0);
  const headings = series.map((p) => p.heading).filter((h) => h !== null);
  return {
    durationSec: round((session.durationMs || 0) / 1000, 2),
    samples: series.length,
    jerkRms: round(rms(jerks)),
    peakAccelG: round(mags.length ? Math.max(...mags) : 0),
    harshCount: mags.filter((m) => m > HARSH_G).length,
    rollRms: round(rms(series.map((p) => p.gx || 0))),
    pitchRms: round(rms(series.map((p) => p.gy || 0))),
    yawRms: round(rms(series.map((p) => p.gz || 0))),
    vibrationG: round(std(mags), 4),
    headingRangeDeg: headings.length ? round(Math.max(...headings) - Math.min(...headings), 1) : null,
  };
}

/** Lower-is-better score: <= good -> 100, good..limit -> 100..70, limit..2*limit -> 70..0. */
function scoreLowerBetter(x, good, limit) {
  if (x <= good) return 100;
  const lim = Math.max(limit, good + 1e-6);
  if (x <= lim) return 100 - ((x - good) / (lim - good)) * 30;
  return Math.max(0, 70 - ((x - lim) / lim) * 70);
}

function limitsFor(key, profile) {
  const ref = profile?.features?.[key];
  if (ref && Number.isFinite(ref.max) && Number.isFinite(ref.limit)) {
    return { good: ref.max, limit: ref.limit, source: "reference", refMean: ref.mean };
  }
  return { ...DEFAULT_LIMITS[key], source: "default" };
}

function scoreGroup(keys, features, profile, failures) {
  const details = keys.map((key) => {
    const lim = limitsFor(key, profile);
    const score = Math.round(scoreLowerBetter(features[key], lim.good, lim.limit));
    if (features[key] > lim.limit) {
      failures.push(`${LABELS[key]} ${features[key]} exceeds ${lim.source} limit ${round(lim.limit, 3)}`);
    }
    return { key, label: LABELS[key], value: features[key], ...lim, score };
  });
  return { score: Math.round(mean(details.map((d) => d.score))), details };
}

function scoreTime(durationSec, profile, failures) {
  const ref = profile?.features?.durationSec;
  const minSec = ref ? round(ref.min * 0.75, 1) : DEFAULT_TIME.minSec;
  const maxSec = ref ? round(ref.max * 1.25, 1) : DEFAULT_TIME.maxSec;
  let score = 100;
  if (durationSec > maxSec) {
    score = Math.max(0, 100 - ((durationSec - maxSec) / maxSec) * 150);
    failures.push(`Test took ${durationSec}s, longer than ${maxSec}s`);
  } else if (durationSec < minSec) {
    score = Math.max(0, 100 - ((minSec - durationSec) / minSec) * 150);
    failures.push(`Test took ${durationSec}s, shorter than ${minSec}s`);
  }
  return { score: Math.round(score), minSec, maxSec, source: ref ? "reference" : "default" };
}

/** Full analysis for one RTDB test node. Returns { session, series, features, turnAnalysis }. */
function analyzeTest(test, profile = null) {
  const { session, series } = normalizeTest(test);
  if (series.length < 3) return { session, series, features: null, turnAnalysis: null };
  const features = computeFeatures(series, session);
  const detection = extractTurns(series, session, profile?.detection || {});
  const turnAnalysis = evaluateTurns(detection, profile);
  features.turnCount = turnAnalysis.turnCount;
  features.avgTurnDurationMs = turnAnalysis.avgDurationMs;
  return { session, series, features, turnAnalysis };
}

/** Score an analysed test. */
function evaluateAnalysis(analysis, profile = null) {
  const { features, turnAnalysis } = analysis;
  const failures = [...turnAnalysis.failures];
  const smooth = scoreGroup(["jerkRms", "peakAccelG", "harshCount"], features, profile, failures);
  const stable = scoreGroup(["rollRms", "pitchRms", "vibrationG"], features, profile, failures);
  const time = scoreTime(features.durationSec, profile, failures);

  const scores = { turns: turnAnalysis.score, smoothness: smooth.score, stability: stable.score, time: time.score };
  const overallScore = Math.round(
    scores.turns * WEIGHTS.turns +
      scores.smoothness * WEIGHTS.smoothness +
      scores.stability * WEIGHTS.stability +
      scores.time * WEIGHTS.time
  );
  const result = overallScore >= PASS_THRESHOLD && turnAnalysis.sequenceOk ? "PASS" : "FAIL";
  if (overallScore < PASS_THRESHOLD) failures.unshift(`Overall score ${overallScore} is below ${PASS_THRESHOLD}`);

  return {
    result,
    overallScore,
    scores,
    weights: WEIGHTS,
    breakdown: { smoothness: smooth.details, stability: stable.details, time },
    failures,
    warnings: turnAnalysis.warnings,
    usedReference: !!profile,
  };
}

/** Compact metrics saved to RTDB (no raw series). */
function summarizeMetrics(analysis) {
  const { session, features, turnAnalysis } = analysis;
  return {
    ...features,
    sampleRateHz: session.sampleRateHz,
    durationMs: session.durationMs,
    turnMethod: turnAnalysis.method,
    expectedSequence: turnAnalysis.expectedSequence,
    detectedSequence: turnAnalysis.detectedSequence,
    sequenceOk: turnAnalysis.sequenceOk,
    turns: turnAnalysis.turns.map(({ issues, ...t }) => ({ ...t, issues: issues.length ? issues : null })),
  };
}

export {
  analyzeTest,
  evaluateAnalysis,
  summarizeMetrics,
  computeFeatures,
  PASS_THRESHOLD,
  DEFAULT_LIMITS,
  WEIGHTS,
  LABELS,
};
